import {Typography, Stack, Box} from '@mui/material'
import {LineChart, Line, ResponsiveContainer} from 'recharts'
import {
  PerformanceCellContainer,
  PerformanceValue,
  PerformanceCount,
  getPerformanceColor,
} from './PerformanceStyled'
import {formatNumber, numberPrecision} from '../../helpers/UtilityHelper'

export const PerformanceCell = ({
  value,
  count,
  label = 'orders',
  totalCount,
  columnName,
}) => {
  if (!value) {
    return (
      <Typography variant='body2' color='text.secondary'>
        -
      </Typography>
    )
  }

  const performanceColor = getPerformanceColor(value, columnName)

  return (
    <PerformanceCellContainer performanceColor={performanceColor}>
      <PerformanceValue performanceColor={performanceColor}>
        {numberPrecision(value, 1)}%
      </PerformanceValue>
      <PerformanceCount>
        {formatNumber(count)} {totalCount ? `/${formatNumber(totalCount)}` : ''} {label}
      </PerformanceCount>
    </PerformanceCellContainer>
  )
}

// Simpler variant: colored percentage text with optional count subtitle
export const SimplePerformanceCell = ({
  value,
  columnName,
  suffix = '%',
  count,
  totalCount,
}) => {
  if (value === null || value === undefined) {
    return (
      <Typography variant='body2' color='text.secondary'>
        -
      </Typography>
    )
  }

  const color = getPerformanceColor(value, columnName)

  return (
    <Stack spacing={0} alignItems='center'>
      <Typography variant='body2' fontWeight={600} sx={{color}}>
        {numberPrecision(value, 1)}
        {suffix}
      </Typography>
      {count !== undefined && (
        <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.8rem', lineHeight: 1.3}}>
          {formatNumber(count)}{totalCount !== undefined ? `/${formatNumber(totalCount)}` : ''}
        </Typography>
      )}
    </Stack>
  )
}

// Gap cell: shows + or - sign, colored
export const GapCell = ({value}) => {
  if (value === null || value === undefined || isNaN(value)) {
    return (
      <Typography variant='body2' color='text.secondary'>
        -
      </Typography>
    )
  }

  const color = value > 0 ? '#ef4444' : value < 0 ? '#10b981' : '#6b7280'
  const sign = value > 0 ? '+' : ''

  return (
    <Typography variant='body2' fontWeight={600} sx={{color}}>
      {sign}
      {numberPrecision(value, 1)}%
    </Typography>
  )
}

// Sparkline cell: tiny line chart for 30d trend
export const SparklineCell = ({data}) => {
  if (!data || data.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        -
      </Typography>
    )
  }

  const chartData = data.map((v, i) => ({v}))
  const lastValue = data[data.length - 1]
  const firstValue = data[0]
  const trendColor = lastValue >= firstValue ? '#10b981' : '#ef4444'

  return (
    <Box sx={{width: 90, height: 32, display: 'inline-block'}}>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={chartData}>
          <Line
            type='monotone'
            dataKey='v'
            stroke={trendColor}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
